import React, { Fragment, useCallback, useEffect, useState } from "react";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@components/buttons/Button/Button";
import LoadingSpinner from "@components/indicators/LoadingSpinner/LoadingSpinner";
import { Card } from "@mui/material";
import styles from "@styles/CommonEditorPageLayout.module.css";
import TextInput from "@components/inputs/TextInput/TextInput";
import { isValidEmail } from "@util/validators";
import { fetchBusiness, setBusinessAdmin } from "@services/businessService";
import { Business } from "@models/Business";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";

const SetBusinessAdminPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const mode = searchParams.get("mode");
    const [business, setBusiness] = useState<Business>();

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [adminEmail, setAdminEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) {
                    throw new Error("No ID.");
                }
                const data = await fetchBusiness(id);
                setBusiness(data);
            } catch (err) {
                setError("Failed to fetch memorial info.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [id]);

    const validateEmail = (val: string) => {
        return isValidEmail(val);
    };

    // Handlers for updating address fields and performing validation
    const handleEmailChange = useCallback((val: string) => {
        setAdminEmail(val);
        setEmailError(validateEmail(val));
    }, []);

    const handleSubmit = async () => {
        let isValid = true;

        // Validate Street Address
        const errorMessage = validateEmail(adminEmail);
        setEmailError(errorMessage);
        if (errorMessage) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setSubmitErrorMessage("");

                if (!id) {
                    throw new Error("No ID.");
                }

                await setBusinessAdmin(id, adminEmail);

                setIsSaving(false);
                alert("Success! Invitation sent.");
                navigate(-1);
            } catch (err) {
                setSubmitErrorMessage("Failed to set admin. Please try again.");
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleCancel = async () => {
        navigate(-1);
    };

    const renderContent = () => {
        if (loading || isSaving || error) {
            return <LoadSaveErrorDisplay isLoading={loading} isSaving={isSaving} error={error} />;
        }

        if (!business) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>No Business info found.</div>;
        }

        return (
            <>
                <h1>{`${business.name}`}</h1>
                <h2>Set Business Admin</h2>
                <div className={styles.contentContainer}>
                    <Card className={styles.cardContainer}>
                        <TextInput
                            label="Admin Email *"
                            id="adminEmail"
                            value={adminEmail}
                            onChange={handleEmailChange}
                            placeholder="Enter email..."
                            errorMessage={emailError}
                        />
                        <div className={styles.formButtons}>
                            <Button
                                variant="text"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                            >
                                Save
                            </Button>
                        </div>
                        {submitErrorMessage && (
                            <p className={styles.errorMessage}>{submitErrorMessage}</p>
                        )}
                    </Card>
                    <div className={styles.messageContainer}>
                        {mode === "change" && (
                            <>
                                <p className={styles.errorMessage}>Note: There is already an existing pending invitation.</p>
                                <p className={styles.errorMessage}>Setting new admin will cancel the pending invitation</p>
                            </>
                        )}
                        <p>Once business admin is set and accepted, it cannot be changed.</p>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={commonStyles.container}>
            {renderContent()}
        </div>
    );
};

export default SetBusinessAdminPage;
